/**
 * Service for interacting with the Solana Tracker Data API
 */

// Import the real client from the npm package
import { Client } from '@solana-tracker/data-api';
import { proxyService } from './proxyService';
import { env } from '../config/env';
import {
    SOLANA_TRACKER_CONFIG,
    rateLimiter,
    validateApiKey,
    createFetchOptions,
    handleApiError,
    shouldRetry,
    getRetryDelay,
    apiCache
} from './solanaTrackerConfig';

// Initialize the API client with API key from environment variables
const API_KEY = env.SOLANA_TRACKER_API_KEY || '';

if (!API_KEY) {
    console.error('❌ VITE_SOLANA_TRACKER_API_KEY is not set in .env file');
    console.warn('🔧 Please add VITE_SOLANA_TRACKER_API_KEY=your_api_key to your .env file');
}

// Create the client instance with the API key from .env
const client = new Client({ apiKey: API_KEY || '' });

/**
 * Returns the client instance for direct use
 */
export const getClient = () => client;

/**
 * Check API connection status
 */
export const checkApiConnection = async (): Promise<boolean> => {
    try {
        if (!API_KEY) {
            console.error('❌ Cannot check API connection - API key is missing');
            return false;
        }

        const response = await fetch(`${BASE_URL}/trending/1h`, {
            method: 'GET',
            headers: {
                'x-api-key': API_KEY,
                'Content-Type': 'application/json'
            }
        });

        const isConnected = response.ok;
        console.log(`🔗 Solana Tracker API connection: ${isConnected ? '✅ Connected' : '❌ Failed'}`);

        if (!isConnected) {
            console.error(`❌ API Error: ${response.status} ${response.statusText}`);
        }

        return isConnected;
    } catch (error) {
        console.error('❌ API connection check failed:', error);
        return false;
    }
};

/**
 * Unified API client with rate limiting, caching, and robust error handling
 */
export class SolanaTrackerApiClient {
    private static instance: SolanaTrackerApiClient;

    public static getInstance(): SolanaTrackerApiClient {
        if (!SolanaTrackerApiClient.instance) {
            SolanaTrackerApiClient.instance = new SolanaTrackerApiClient();
        }
        return SolanaTrackerApiClient.instance;
    }

    /**
     * Make an API request with rate limiting, caching, and retry logic
     */
    async request(endpoint: string, options: RequestInit = {}, useCache: boolean = true): Promise<any> {
        if (!validateApiKey()) {
            throw new Error('Solana Tracker API key is not configured. Please set VITE_SOLANA_TRACKER_API_KEY in your .env file');
        }

        const cacheKey = `${endpoint}:${JSON.stringify(options)}`;

        // Check cache first
        if (useCache) {
            const cachedData = apiCache.get(cacheKey);
            if (cachedData) {
                console.log(`📋 Cache hit for ${endpoint}`);
                return cachedData;
            }
        }

        // Rate limiting
        await rateLimiter.waitForSlot();

        const url = `${SOLANA_TRACKER_CONFIG.BASE_URL}${endpoint}`;
        const fetchOptions = createFetchOptions(options);

        let lastError: any;

        // Retry logic
        for (let attempt = 0; attempt < SOLANA_TRACKER_CONFIG.RATE_LIMIT.RETRY_ATTEMPTS; attempt++) {
            try {
                console.log(`🔄 API Request (attempt ${attempt + 1}): ${endpoint}`);

                const response = await fetch(url, fetchOptions);

                if (!response.ok) {
                    const errorText = await response.text().catch(() => 'Unknown error');
                    throw new Error(`${response.status} ${response.statusText} - ${errorText}`);
                }

                const data = await response.json();

                // Cache successful responses
                if (useCache) {
                    apiCache.set(cacheKey, data, SOLANA_TRACKER_CONFIG.CACHE.TOKEN_DATA_TTL);
                }

                console.log(`✅ API Success: ${endpoint}`);
                return data;

            } catch (error) {
                lastError = error;

                if (!shouldRetry(error, attempt)) {
                    break;
                }

                const delay = getRetryDelay(attempt);
                console.warn(`⚠️ API attempt ${attempt + 1} failed, retrying in ${delay}ms:`, error);
                await new Promise(resolve => setTimeout(resolve, delay));
            }
        }

        handleApiError(lastError, endpoint);
    }

    /**
     * Make a request that returns a standardized response format
     */
    async requestWithFallback<T>(endpoint: string, fallbackData: T, options: RequestInit = {}): Promise<ApiResponse<T>> {
        try {
            const data = await this.request(endpoint, options);
            return {
                status: 'success',
                data
            };
        } catch (error) {
            console.warn(`⚠️ API request failed, using fallback data for ${endpoint}:`, error);
            return {
                status: 'success',
                data: fallbackData
            };
        }
    }
}

// Global API client instance
const apiClient = SolanaTrackerApiClient.getInstance();

/**
 * Legacy wrapper for backward compatibility
 */
const makeApiRequest = async (endpoint: string, options: RequestInit = {}): Promise<Response> => {
    if (!validateApiKey()) {
        throw new Error('Solana Tracker API key is not configured. Please set VITE_SOLANA_TRACKER_API_KEY in your .env file');
    }

    const url = `${SOLANA_TRACKER_CONFIG.BASE_URL}${endpoint}`;
    const fetchOptions = createFetchOptions(options);

    const response = await fetch(url, fetchOptions);

    if (!response.ok) {
        const errorText = await response.text().catch(() => 'Unknown error');
        throw new Error(`Solana Tracker API error: ${response.status} ${response.statusText} - ${errorText}`);
    }

    return response;
};

// Define the base URL for the Solana tracker API
const BASE_URL = 'https://data.solanatracker.io';

// Response interface for API calls
interface ApiResponse<T> {
    status: 'success' | 'error';
    data?: T;
    error?: string;
}

// Define interfaces to match data-api-sdk
export interface OHLCVData {
    open: number;
    high: number;
    low: number;
    close: number;
    volume: number;
    time: number;
}

export interface ApiChartResponse {
    oclhv: OHLCVData[];
}

// Our wrapper response type for consistent API responses
export interface ChartResponse {
    status: string;
    data?: OHLCVData[];
    error?: string;
}

// Add these interfaces
interface TokenDetails {
    name: string;
    symbol: string;
    mint: string;
    uri?: string;
    decimals: number;
    hasFileMetaData?: boolean;
    createdOn?: string;
    description?: string;
    image?: string | null;
    showName?: boolean;
    twitter?: string;
    website?: string;
    strictSocials?: Record<string, string>;
}

interface TokenWithBalance {
    token: TokenDetails;
    balance: number;
    value: number;
    price?: {
        quote: number;
        usd: number;
    };
    pools?: any[];
    events?: Record<string, { priceChangePercentage: number }>;
    risk?: any;
    buys?: number;
    sells?: number;
    txns?: number;
    holders?: number;
}

interface WalletTokensResponse {
    tokens: TokenWithBalance[];
    total: number;
    totalSol: number;
}

// Define a type for PnL response
interface PnLPosition {
    pnl?: {
        usd: number;
        sol?: number;
    };
    invested?: {
        usd: number;
        sol?: number;
    };
    // other properties...
}

interface PnLSummary {
    // Add the properties that we're setting
    totalPnL?: number;
    totalInvested?: number;
    pnlPercentage?: number;
    // Add other known properties if needed
}

interface PnLResponse {
    positions?: PnLPosition[];
    summary?: PnLSummary;
    // other properties...
}

// Cache for wallet token data to avoid repeated API calls
const walletTokensCache = new Map<string, { data: any; timestamp: number }>();
const CACHE_DURATION = 30000; // 30 seconds cache

// Clean up expired cache entries periodically
setInterval(() => {
    const now = Date.now();
    const expiredKeys: string[] = [];

    walletTokensCache.forEach((value, key) => {
        if (now - value.timestamp > CACHE_DURATION) {
            expiredKeys.push(key);
        }
    });

    expiredKeys.forEach(key => {
        walletTokensCache.delete(key);
    });

    if (expiredKeys.length > 0) {
        console.log(`🧹 Cleaned up ${expiredKeys.length} expired wallet cache entries`);
    }
}, 60000); // Clean up every minute

/**
 * Clear wallet tokens cache for specific addresses or all
 * @param walletAddresses Optional array of wallet addresses to clear, if not provided clears all
 */
export const clearWalletTokensCache = (walletAddresses?: string[]) => {
    if (walletAddresses && Array.isArray(walletAddresses)) {
        walletAddresses.forEach(address => {
            walletTokensCache.delete(address);
        });
        console.log(`🧹 Cleared cache for ${walletAddresses.length} specific wallets`);
    } else {
        walletTokensCache.clear();
        console.log(`🧹 Cleared all wallet tokens cache`);
    }
};

/**
 * CHARTS ENDPOINTS
 */

/**
 * Get historical chart data for a token using direct API calls
 * 
 * CRITICAL DATA SMOOTHING: This function implements comprehensive data smoothing to ensure
 * perfect candle continuity while preserving market volatility characteristics:
 * 
 * 1. CONTINUITY ENFORCEMENT: Every candle's open equals the previous candle's close
 * 2. VOLATILITY PRESERVATION: Extreme movements are capped but not eliminated
 * 3. OHLC VALIDATION: All candle relationships are mathematically valid
 * 4. GAP SMOOTHING: Price gaps are bridged with smooth transitions
 * 5. NO BAD CANDLES: Discontinuous candles are never displayed
 * 
 * @param tokenMint Token mint address
 * @param timeframe Timeframe (1s, 5s, 15s, 30s, 1m, 5m, 15m, 30m, 1h, 2h, 4h, 6h, 8h, 12h, 1d, 3d, 1w, 1M)
 * @param from Start time in seconds
 * @param to End time in seconds
 * @returns Promise with OHLCV data (smoothed and stitched for perfect continuity)
 */
export const getTokenChartData = async (
    tokenMint: string,
    timeframe: string = '1h',
    from?: number,
    to?: number
): Promise<ApiResponse<OHLCVData[]>> => {
    console.log(`📊 [getTokenChartData] Starting request for ${tokenMint}, timeframe: ${timeframe}`);

    // Calculate proper time range based on timeframe if not provided
    const now = Math.floor(Date.now() / 1000);
    let actualFrom = from;
    let actualTo = to || now;

    if (!actualFrom) {
        // Set appropriate default time ranges based on timeframe
        switch (timeframe) {
            case '1s':
            case '5s':
            case '15s':
            case '30s':
                actualFrom = now - (24 * 60 * 60); // 24 hours for second-level timeframes
                break;
            case '1m':
            case '5m':
            case '15m':
                actualFrom = now - (7 * 24 * 60 * 60); // 7 days for minute-level timeframes
                break;
            case '30m':
            case '1h':
            case '2h':
            case '4h':
                actualFrom = now - (30 * 24 * 60 * 60); // 30 days for hour-level timeframes
                break;
            default:
                actualFrom = now - (90 * 24 * 60 * 60); // 90 days for daily/weekly timeframes
                break;
        }
    }

    const startTime = performance.now();

    try {
        console.log(`📈 [getTokenChartData] Fetching data for ${tokenMint}, timeframe: ${timeframe}, from: ${new Date(actualFrom * 1000).toISOString()} to: ${new Date(actualTo * 1000).toISOString()}`);

        // Build the API URL with parameters
        const params = new URLSearchParams();
        if (timeframe) params.append('type', timeframe);
        if (actualFrom) params.append('time_from', actualFrom.toString());
        if (actualTo) params.append('time_to', actualTo.toString());
        params.append('removeOutliers', 'true'); // Enable outlier removal

        const url = `${BASE_URL}/chart/${tokenMint}?${params.toString()}`;
        console.log(`🔗 [getTokenChartData] API URL: ${url}`);

        // Make the API request
        const response = await fetch(url, {
            method: 'GET',
            headers: {
                'x-api-key': API_KEY,
                'Content-Type': 'application/json'
            }
        });

        const endTime = performance.now();
        console.log(`✅ [getTokenChartData] API call completed in ${(endTime - startTime).toFixed(1)}ms, status: ${response.status}`);

        if (!response.ok) {
            if (response.status === 429) {
                console.warn(`🚫 [getTokenChartData] Rate limit exceeded for ${tokenMint}`);
                return {
                    status: 'error',
                    error: 'Rate limit exceeded. Please try again later.'
                };
            } else if (response.status === 404) {
                console.warn(`🔍 [getTokenChartData] Token not found: ${tokenMint}`);
                return {
                    status: 'error',
                    error: 'Token not found or no chart data available'
                };
            }
            throw new Error(`Solana Tracker API error: ${response.status} ${response.statusText}`);
        }

        const result = await response.json();
        console.log(`📊 [getTokenChartData] Raw API response structure:`, {
            hasOclhv: !!result.oclhv,
            oclhvLength: result.oclhv?.length || 0,
            sampleData: result.oclhv?.[0]
        });

        // Check if we have valid data
        if (!result || !result.oclhv || !Array.isArray(result.oclhv)) {
            console.warn(`⚠️ [getTokenChartData] No valid data returned for ${tokenMint}`, result);
            return {
                status: 'error',
                error: 'No chart data available'
            };
        }

        // Transform the data to our expected format and ensure chronological order
        const chartData: OHLCVData[] = result.oclhv
            .map((candle: any) => ({
                time: candle.time, // Already in seconds
                open: candle.open,
                high: candle.high,
                low: candle.low,
                close: candle.close,
                volume: candle.volume || 0
            }))
            .sort((a: OHLCVData, b: OHLCVData) => a.time - b.time); // CRITICAL: Ensure chronological order

        // Filter out invalid data points
        const validData = chartData.filter(candle => {
            // Remove candles with zero or negative prices
            if (candle.open <= 0 || candle.high <= 0 || candle.low <= 0 || candle.close <= 0) {
                return false;
            }

            // Check for impossible OHLC relationships
            if (candle.high < candle.low ||
                candle.high < candle.open ||
                candle.high < candle.close ||
                candle.low > candle.open ||
                candle.low > candle.close) {
                return false;
            }

            return true;
        });

        console.log(`📊 [getTokenChartData] Processed ${validData.length}/${chartData.length} valid candles for ${tokenMint}`);

        // CRITICAL: Smooth and stitch candle data to ensure perfect continuity
        if (validData.length > 1) {
            console.log(`🔗 [getTokenChartData] Smoothing and stitching ${validData.length} candles for perfect continuity`);

            const smoothedData: OHLCVData[] = [];
            let smoothedCandles = 0;
            let removedCandles = 0;

            for (let i = 0; i < validData.length; i++) {
                if (i === 0) {
                    // First candle is always accepted as-is
                    smoothedData.push(validData[i]);
                    continue;
                }

                const prevCandle = smoothedData[smoothedData.length - 1];
                const currentCandle = validData[i];
                const expectedOpen = prevCandle.close;
                const actualOpen = currentCandle.open;

                // Check if there's a continuity break
                const openDiscrepancy = Math.abs((actualOpen - expectedOpen) / expectedOpen);

                if (openDiscrepancy > 0.001) { // 0.1% tolerance for rounding
                    // Calculate the price gap
                    const priceGap = actualOpen - expectedOpen;
                    const gapPercentage = (openDiscrepancy * 100);

                    console.warn(`🔧 [getTokenChartData] Smoothing discontinuous candle ${i}: open ${actualOpen.toFixed(8)} -> ${expectedOpen.toFixed(8)} (${gapPercentage.toFixed(3)}% gap)`);

                    // SMOOTH THE DATA: Force continuity while preserving the candle's movement
                    const smoothedCandle: OHLCVData = {
                        ...currentCandle,
                        open: expectedOpen, // Force open to equal previous close
                        // Adjust high and low to maintain valid OHLC relationships
                        high: Math.max(currentCandle.high, expectedOpen, currentCandle.close),
                        low: Math.min(currentCandle.low, expectedOpen, currentCandle.close)
                    };

                    // If the gap is extreme (>50%), consider if we should interpolate
                    if (gapPercentage > 50) {
                        console.warn(`🚨 [getTokenChartData] EXTREME GAP detected (${gapPercentage.toFixed(1)}%) - considering interpolation`);

                        // For extreme gaps, we can either:
                        // 1. Remove the candle entirely (current approach)
                        // 2. Create interpolated candles to bridge the gap
                        // 3. Smooth the transition

                        // For now, let's smooth the transition by adjusting the close price
                        // to be closer to the expected range
                        const maxAllowedMove = expectedOpen * 0.5; // 50% max move
                        const actualMove = currentCandle.close - expectedOpen;

                        if (Math.abs(actualMove) > maxAllowedMove) {
                            // Cap the movement to prevent extreme jumps
                            const cappedClose = expectedOpen + (actualMove > 0 ? maxAllowedMove : -maxAllowedMove);
                            smoothedCandle.close = cappedClose;
                            smoothedCandle.high = Math.max(smoothedCandle.high, cappedClose);
                            smoothedCandle.low = Math.min(smoothedCandle.low, cappedClose);

                            console.log(`📉 [getTokenChartData] Capped extreme movement: ${currentCandle.close.toFixed(8)} -> ${cappedClose.toFixed(8)}`);
                        }
                    }

                    // Validate the smoothed candle has proper OHLC relationships
                    if (smoothedCandle.high < smoothedCandle.low ||
                        smoothedCandle.high < smoothedCandle.open ||
                        smoothedCandle.high < smoothedCandle.close ||
                        smoothedCandle.low > smoothedCandle.open ||
                        smoothedCandle.low > smoothedCandle.close) {

                        console.warn(`🔧 [getTokenChartData] Fixing invalid OHLC relationships in smoothed candle`);
                        smoothedCandle.high = Math.max(smoothedCandle.open, smoothedCandle.close);
                        smoothedCandle.low = Math.min(smoothedCandle.open, smoothedCandle.close);
                    }

                    smoothedData.push(smoothedCandle);
                    smoothedCandles++;
                } else {
                    // Candle is already continuous, add as-is
                    smoothedData.push(currentCandle);
                }
            }

            // Replace validData with the smoothed version
            validData.length = 0;
            validData.push(...smoothedData);

            if (smoothedCandles > 0) {
                console.log(`🔧 [getTokenChartData] SMOOTHED ${smoothedCandles} discontinuous candles. Final count: ${validData.length} candles`);
            } else {
                console.log(`✅ [getTokenChartData] All candles were already continuous - ${validData.length} candles`);
            }

            // Final verification - this should NEVER find errors now
            let continuityErrors = 0;
            for (let i = 1; i < validData.length; i++) {
                const prevClose = validData[i - 1].close;
                const currentOpen = validData[i].open;
                const discrepancy = Math.abs((currentOpen - prevClose) / prevClose);

                if (discrepancy > 0.001) {
                    console.error(`❌ [getTokenChartData] CONTINUITY ERROR AFTER SMOOTHING at candle ${i}: prev close ${prevClose.toFixed(8)} != current open ${currentOpen.toFixed(8)}`);
                    continuityErrors++;
                }
            }

            if (continuityErrors === 0) {
                console.log(`✅ [getTokenChartData] Final verification passed - all ${validData.length} candles are perfectly stitched`);
            } else {
                console.error(`❌ [getTokenChartData] CRITICAL ERROR: Found ${continuityErrors} continuity errors after smoothing!`);
            }
        }

        if (validData.length === 0) {
            console.warn(`⚠️ [getTokenChartData] No valid candles after filtering for ${tokenMint}`);
            return {
                status: 'error',
                error: 'No valid chart data after filtering'
            };
        }

        // Log sample data for debugging
        if (validData.length > 0) {
            const firstCandle = validData[0];
            const lastCandle = validData[validData.length - 1];
            console.log(`📊 [getTokenChartData] Sample data - First: ${new Date(firstCandle.time * 1000).toISOString()}, Price: $${firstCandle.close.toFixed(8)}`);
            console.log(`📊 [getTokenChartData] Sample data - Last: ${new Date(lastCandle.time * 1000).toISOString()}, Price: $${lastCandle.close.toFixed(8)}`);
        }

        return {
            status: 'success',
            data: validData
        };

    } catch (error: any) {
        const endTime = performance.now();
        console.error(`❌ [getTokenChartData] Error fetching chart data for ${tokenMint}: ${error.message} (${(endTime - startTime).toFixed(1)}ms)`);

        return {
            status: 'error',
            error: error.message || 'Unknown error fetching chart data'
        };
    }
};

/**
 * Get OHLCV data for a specific token and pool
 */
export const getPoolChartData = async (
    tokenAddress: string,
    poolAddress: string,
    timeframe: string = '1h',
    days: number = 7
) => {
    try {
        const now = Math.floor(Date.now() / 1000);
        const startTime = now - (days * 24 * 60 * 60);

        const params = new URLSearchParams();
        if (timeframe) params.append('type', timeframe);
        params.append('time_from', startTime.toString());
        params.append('time_to', now.toString());

        const url = `${BASE_URL}/chart/${tokenAddress}/${poolAddress}?${params.toString()}`;

        const response = await fetch(url, {
            method: 'GET',
            headers: {
                'x-api-key': API_KEY,
                'Content-Type': 'application/json'
            }
        });

        if (!response.ok) {
            throw new Error(`Solana Tracker API error: ${response.status} ${response.statusText}`);
        }

        return await response.json();
    } catch (error) {
        console.error('Error fetching pool chart data:', error);
        throw error;
    }
};

/**
 * Get holder count chart data
 */
export const getHolderCountChart = async (
    tokenAddress: string,
    days: number = 30
) => {
    try {
        const now = Math.floor(Date.now() / 1000);
        const startTime = now - (days * 24 * 60 * 60);

        const params = new URLSearchParams();
        params.append('type', '1d'); // Daily data
        params.append('time_from', startTime.toString());
        params.append('time_to', now.toString());

        const url = `${BASE_URL}/holders/chart/${tokenAddress}?${params.toString()}`;

        const response = await fetch(url, {
            method: 'GET',
            headers: {
                'x-api-key': API_KEY,
                'Content-Type': 'application/json'
            }
        });

        if (!response.ok) {
            throw new Error(`Solana Tracker API error: ${response.status} ${response.statusText}`);
        }

        return await response.json();
    } catch (error) {
        console.error('Error fetching holder count chart:', error);
        throw error;
    }
};

/**
 * PRICES ENDPOINTS
 */

/**
 * Get current price of a token
 */
export const getTokenPrice = async (tokenAddress: string) => {
    try {
        const url = `${BASE_URL}/price?token=${tokenAddress}&priceChanges=true`;

        const response = await fetch(url, {
            method: 'GET',
            headers: {
                'x-api-key': API_KEY,
                'Content-Type': 'application/json'
            }
        });

        if (!response.ok) {
            throw new Error(`Solana Tracker API error: ${response.status} ${response.statusText}`);
        }

        return await response.json();
    } catch (error) {
        console.error('Error fetching token price:', error);
        throw error;
    }
};

/**
 * Get historical price data
 */
export const getHistoricalPrices = async (tokenAddress: string) => {
    try {
        const url = `${BASE_URL}/price/history?token=${tokenAddress}`;

        const response = await fetch(url, {
            method: 'GET',
            headers: {
                'x-api-key': API_KEY,
                'Content-Type': 'application/json'
            }
        });

        if (!response.ok) {
            throw new Error(`Solana Tracker API error: ${response.status} ${response.statusText}`);
        }

        return await response.json();
    } catch (error) {
        console.error('Error fetching historical prices:', error);
        throw error;
    }
};

/**
 * Get price range (highest/lowest) in a time period
 */
export const getPriceRange = async (tokenAddress: string, daysAgo: number) => {
    try {
        const now = Math.floor(Date.now() / 1000);
        const past = now - (daysAgo * 24 * 60 * 60);

        const url = `${BASE_URL}/price/history/range?token=${tokenAddress}&time_from=${past}&time_to=${now}`;

        const response = await fetch(url, {
            method: 'GET',
            headers: {
                'x-api-key': API_KEY,
                'Content-Type': 'application/json'
            }
        });

        if (!response.ok) {
            throw new Error(`Solana Tracker API error: ${response.status} ${response.statusText}`);
        }

        return await response.json();
    } catch (error) {
        console.error('Error fetching price range:', error);
        throw error;
    }
};

/**
 * TOKENS ENDPOINTS
 */

/**
 * Get comprehensive token info including pools, price, market cap, etc.
 * Uses the /tokens/{tokenAddress} endpoint which provides complete token data
 */
export async function getTokenInfo(tokenAddress: string): Promise<ApiResponse<any>> {
    if (!tokenAddress) {
        console.error('getTokenInfo: Invalid or missing token address');
        return {
            status: 'error',
            error: 'Invalid token address'
        };
    }

    try {
        console.log(`🔍 Fetching comprehensive token info for: ${tokenAddress}`);

        // Use the comprehensive endpoint that includes pools, price, market cap, etc.
        const response = await fetch(`${BASE_URL}/tokens/${tokenAddress}`, {
            method: 'GET',
            headers: {
                'x-api-key': API_KEY,
                'Content-Type': 'application/json'
            }
        });

        if (!response.ok) {
            throw new Error(`Solana Tracker API error: ${response.status} ${response.statusText}`);
        }

        const data = await response.json();

        // Log the data structure for debugging
        console.log(`✅ Token info fetched for ${data.token?.symbol || tokenAddress}:`, {
            name: data.token?.name,
            symbol: data.token?.symbol,
            price: data.pools?.[0]?.price?.usd,
            marketCap: data.pools?.[0]?.marketCap?.usd,
            liquidity: data.pools?.[0]?.liquidity?.usd
        });

        return {
            status: 'success',
            data
        };
    } catch (error) {
        console.error('Error fetching comprehensive token info:', error);
        return {
            status: 'error',
            error: error instanceof Error ? error.message : 'Unknown error'
        };
    }
}

/**
 * Get trending tokens
 */
export const getTrendingTokens = async (timeframe: string = '1h') => {
    try {
        // Try using the client first
        try {
            return await client.getTrendingTokens(timeframe);
        } catch (clientError) {

            // Fallback to our proxy endpoint
            const functionsUrl = import.meta.env.VITE_SUPABASE_FUNCTIONS_URL || 'https://fuczyhbturoiyzposwkd.supabase.co/functions/v1';
            const proxyUrl = `${functionsUrl}/solana-tracker-proxy/tokens/trending?timeframe=${timeframe}`;

            const response = await fetch(proxyUrl, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                }
            });

            if (!response.ok) {
                throw new Error(`Proxy request failed: ${response.statusText}`);
            }

            const data = await response.json();
            return data;
        }
    } catch (error) {
        console.error('Error fetching trending tokens:', error);

        // Return empty array instead of throwing to prevent UI errors
        return [];
    }
};

/**
 * Get token holders
 */
export const getTokenHolders = async (tokenAddress: string) => {
    try {
        const response = await makeApiRequest(`/tokens/${tokenAddress}/holders`);
        return await response.json();
    } catch (error) {
        console.error('Error fetching token holders:', error);
        throw error;
    }
};

/**
 * Search for tokens with flexible filtering
 */
export const searchTokens = async (params: {
    query: string;
    page?: number;
    limit?: number;
    sortBy?: string;
    sortOrder?: string;
    minLiquidity?: number;
    maxLiquidity?: number;
    minMarketCap?: number;
    maxMarketCap?: number;
    showAllPools?: boolean;
}) => {
    try {
        const searchParams = new URLSearchParams();
        Object.entries(params).forEach(([key, value]) => {
            if (value !== undefined) {
                searchParams.append(key, value.toString());
            }
        });

        const url = `${BASE_URL}/search?${searchParams.toString()}`;

        const response = await fetch(url, {
            method: 'GET',
            headers: {
                'x-api-key': API_KEY,
                'Content-Type': 'application/json'
            }
        });

        if (!response.ok) {
            throw new Error(`Solana Tracker API error: ${response.status} ${response.statusText}`);
        }

        return await response.json();
    } catch (error) {
        console.error('Error searching tokens:', error);
        // Return empty result instead of throwing
        return {
            status: 'error',
            error: error instanceof Error ? error.message : 'Unknown error',
            data: []
        };
    }
};

/**
 * STATS ENDPOINTS
 */

/**
 * Get detailed stats for a token
 * @param tokenAddress The token mint address
 * @returns Detailed token stats including price, market cap, volume, etc.
 */
export async function getTokenDetailedStats(tokenAddress: string): Promise<ApiResponse<any>> {
    if (!tokenAddress) {
        console.error('getTokenDetailedStats: Invalid or missing token address');
        return {
            status: 'error',
            error: 'Invalid token address'
        };
    }

    try {
        // Use direct API call
        const response = await fetch(`${BASE_URL}/stats/${tokenAddress}`, {
            method: 'GET',
            headers: {
                'x-api-key': API_KEY,
                'Content-Type': 'application/json'
            }
        });

        if (!response.ok) {
            throw new Error(`Solana Tracker API error: ${response.status} ${response.statusText}`);
        }

        const data = await response.json();
        return {
            status: 'success',
            data
        };
    } catch (error) {
        console.error('Error fetching token detailed stats:', error);
        return {
            status: 'error',
            error: error instanceof Error ? error.message : 'Unknown error'
        };
    }
}

/**
 * TRADES ENDPOINTS
 */

/**
 * Get trades for a token
 */
export const getTokenTrades = async (tokenAddress: string) => {
    try {
        const response = await makeApiRequest(`/trades/${tokenAddress}?showMeta=true&parseJupiter=true`);
        return await response.json();
    } catch (error) {
        console.error('Error fetching token trades:', error);
        throw error;
    }
};

/**
 * WALLET ENDPOINTS
 */

/**
 * Get wallet information
 */
export const getWalletInfo = async (walletAddress: string) => {
    try {
        const url = `${BASE_URL}/wallet/${walletAddress}`;

        const response = await fetch(url, {
            method: 'GET',
            headers: {
                'x-api-key': API_KEY,
                'Content-Type': 'application/json'
            }
        });

        if (!response.ok) {
            throw new Error(`Solana Tracker API error: ${response.status} ${response.statusText}`);
        }

        return await response.json();
    } catch (error) {
        console.error('Error fetching wallet info:', error);
        throw error;
    }
};

/**
 * Get wallet trade history
 */
export const getWalletTrades = async (walletAddress: string) => {
    try {
        const url = `${BASE_URL}/wallet/${walletAddress}/trades?showMeta=true&parseJupiter=true`;

        const response = await fetch(url, {
            method: 'GET',
            headers: {
                'x-api-key': API_KEY,
                'Content-Type': 'application/json'
            }
        });

        if (!response.ok) {
            throw new Error(`Solana Tracker API error: ${response.status} ${response.statusText}`);
        }

        return await response.json();
    } catch (error) {
        console.error('Error fetching wallet trades:', error);
        throw error;
    }
};

/**
 * PROFIT/LOSS ENDPOINTS
 */

/**
 * Get PnL data for all positions in a wallet
 */
export const getWalletProfitAndLoss = async (walletAddress: string, includeHistory: boolean = true) => {
    try {
        // Validate the wallet address
        if (!walletAddress || typeof walletAddress !== 'string' || walletAddress.trim() === '') {
            console.error('Invalid wallet address provided to getWalletProfitAndLoss');
            return {
                status: 'error',
                error: 'Invalid wallet address',
                data: null
            };
        }

        console.log(`🔍 Fetching P&L data for wallet: ${walletAddress.slice(0, 8)}...`);

        // Use the new robust API client with fallback data
        const fallbackData = {
            tokens: {},
            summary: {
                realized: 0,
                unrealized: 0,
                total: 0,
                totalInvested: 0,
                averageBuyAmount: 0,
                totalWins: 0,
                totalLosses: 0,
                winPercentage: 0,
                lossPercentage: 0
            }
        };

        return await apiClient.requestWithFallback(
            `/pnl/${walletAddress.trim()}`,
            fallbackData
        );
    } catch (error) {
        console.error('❌ Error fetching wallet profit and loss:', error);

        // Return fallback structure to prevent UI crashes
        return {
            status: 'success', // Return success with empty data
            data: {
                tokens: {},
                summary: {
                    realized: 0,
                    unrealized: 0,
                    total: 0,
                    totalInvested: 0,
                    averageBuyAmount: 0,
                    totalWins: 0,
                    totalLosses: 0,
                    winPercentage: 0,
                    lossPercentage: 0
                }
            }
        };
    }
};

/**
 * Get PnL for a specific token in a wallet
 */
export const getTokenProfitAndLoss = async (walletAddress: string, tokenAddress: string) => {
    try {
        // Validate inputs
        if (!walletAddress || !tokenAddress) {
            console.warn('⚠️ Invalid wallet or token address provided to getTokenProfitAndLoss');
            return null;
        }

        const url = `${BASE_URL}/pnl/${walletAddress}/${tokenAddress}`;
        console.log(`📊 Fetching P&L data from: ${url}`);

        let response: Response;
        try {
            response = await makeApiRequest(`/pnl/${walletAddress}/${tokenAddress}`);
        } catch (error: any) {
            // If it's a 404, the user probably doesn't have a position for this token
            if (error.message && error.message.includes('404')) {
                console.log('📊 No P&L data found for this token (404) - user likely has no position');
                return {
                    realized: 0,
                    unrealized: 0,
                    total: 0,
                    current_value: 0
                };
            }
            throw error;
        }

        // Check if response has content
        const contentLength = response.headers.get('content-length');
        if (contentLength === '0') {
            console.log('📊 Empty response from P&L API - returning default values');
            return {
                realized: 0,
                unrealized: 0,
                total: 0,
                current_value: 0
            };
        }

        // Get response text first to check if it's valid JSON
        const responseText = await response.text();

        if (!responseText || responseText.trim() === '') {
            console.log('📊 Empty response body from P&L API - returning default values');
            return {
                realized: 0,
                unrealized: 0,
                total: 0,
                current_value: 0
            };
        }

        try {
            const pnlData = JSON.parse(responseText);
            console.log('✅ P&L data parsed successfully:', pnlData);
            return pnlData;
        } catch (jsonError) {
            console.error('❌ Failed to parse P&L JSON response:', jsonError);
            console.log('📄 Response text:', responseText.substring(0, 200) + '...');

            // Return default values instead of throwing
            return {
                realized: 0,
                unrealized: 0,
                total: 0,
                current_value: 0
            };
        }

    } catch (error) {
        console.error('❌ Error fetching token profit and loss:', error);

        // Return default values instead of throwing to prevent UI crashes
        return {
            realized: 0,
            unrealized: 0,
            total: 0,
            current_value: 0
        };
    }
};

/**
 * Get the first buyers of a token with PnL data
 */
export const getFirstBuyers = async (tokenAddress: string) => {
    try {
        const url = `${BASE_URL}/first-buyers/${tokenAddress}`;

        const response = await fetch(url, {
            method: 'GET',
            headers: {
                'x-api-key': API_KEY,
                'Content-Type': 'application/json'
            }
        });

        if (!response.ok) {
            throw new Error(`Solana Tracker API error: ${response.status} ${response.statusText}`);
        }

        return await response.json();
    } catch (error) {
        console.error('Error fetching first buyers:', error);
        throw error;
    }
};

/**
 * Get wallet tokens with full metadata for multiple wallets in a single batch request
 * 
 * PERFORMANCE IMPROVEMENTS:
 * - Uses caching to avoid repeated API calls for the same wallets within 30 seconds
 * - Implements throttling with 50ms delays between requests to avoid rate limiting
 * - Processes multiple wallets in parallel instead of sequentially
 * - Falls back gracefully if individual requests fail
 * - Automatically cleans up expired cache entries to prevent memory leaks
 * 
 * @param walletAddresses Array of wallet addresses to fetch tokens for
 * @returns Object with wallet addresses as keys and token data as values
 */
export const getBatchWalletTokensWithMetadata = async (walletAddresses: string[]) => {
    try {
        // Validate input
        if (!Array.isArray(walletAddresses) || walletAddresses.length === 0) {
            console.error('Invalid wallet addresses array provided to getBatchWalletTokensWithMetadata');
            return {
                status: 'error',
                error: 'Invalid wallet addresses array',
                data: null
            };
        }

        // Filter out invalid addresses
        const validAddresses = walletAddresses.filter(addr =>
            addr && typeof addr === 'string' && addr.trim() !== ''
        );

        if (validAddresses.length === 0) {
            return {
                status: 'success',
                data: {}
            };
        }

        const now = Date.now();
        const batchData: { [walletAddress: string]: any } = {};
        const addressesToFetch: string[] = [];

        // Check cache first
        validAddresses.forEach(address => {
            const cached = walletTokensCache.get(address);
            if (cached && (now - cached.timestamp) < CACHE_DURATION) {
                console.log(`📋 Using cached data for wallet ${address.slice(0, 8)}...`);
                batchData[address] = cached.data;
            } else {
                addressesToFetch.push(address);
            }
        });

        // If all addresses were cached, return immediately
        if (addressesToFetch.length === 0) {
            console.log(`✅ All ${validAddresses.length} wallets served from cache`);
            return {
                status: 'success',
                data: batchData
            };
        }

        console.log(`🔄 Batch fetching tokens for ${addressesToFetch.length} wallets (${validAddresses.length - addressesToFetch.length} from cache)...`);

        // For now, we'll use Promise.all with individual requests since SolanaTracker doesn't have a batch endpoint
        // But we'll implement throttling and better error handling
        const batchPromises = addressesToFetch.map(async (walletAddress, index) => {
            try {
                // Add small delay between requests to avoid rate limiting
                if (index > 0) {
                    await new Promise(resolve => setTimeout(resolve, 50)); // 50ms delay
                }

                const response = await fetch(`${BASE_URL}/wallet/${walletAddress.trim()}`, {
                    method: 'GET',
                    headers: {
                        'x-api-key': API_KEY,
                        'Content-Type': 'application/json'
                    }
                });

                if (!response.ok) {
                    throw new Error(`Solana Tracker API error: ${response.status} ${response.statusText}`);
                }

                const wallet = await response.json();

                if (!wallet || !wallet.tokens || !Array.isArray(wallet.tokens)) {
                    const emptyData = {
                        tokens: [],
                        total: wallet?.total || 0,
                        totalSol: wallet?.totalSol || 0
                    };

                    // Cache empty result
                    walletTokensCache.set(walletAddress, {
                        data: emptyData,
                        timestamp: now
                    });

                    return {
                        walletAddress,
                        ...emptyData
                    };
                }

                // Process the token data to format it consistently
                const processedTokens = wallet.tokens.map((tokenItem: any) => {
                    const token = tokenItem.token || {};
                    const balance = tokenItem.balance || 0;
                    const value = tokenItem.value || 0;

                    return {
                        mint: token.mint || '',
                        name: token.name || 'Unknown Token',
                        symbol: token.symbol || '',
                        decimals: token.decimals || 9,
                        image: token.image || null,
                        balance: balance.toString(),
                        uiBalance: balance.toString(),
                        value: value,
                        price: tokenItem.price || {
                            quote: 0,
                            usd: 0
                        }
                    };
                });

                const walletData = {
                    tokens: processedTokens,
                    total: wallet.total || 0,
                    totalSol: wallet.totalSol || 0
                };

                // Cache the result
                walletTokensCache.set(walletAddress, {
                    data: walletData,
                    timestamp: now
                });

                return {
                    walletAddress,
                    ...walletData
                };

            } catch (error) {
                console.error(`Error fetching tokens for wallet ${walletAddress}:`, error);
                const errorData = {
                    tokens: [],
                    total: 0,
                    totalSol: 0,
                    error: error instanceof Error ? error.message : 'Unknown error'
                };

                return {
                    walletAddress,
                    ...errorData
                };
            }
        });

        const results = await Promise.all(batchPromises);

        // Add fetched results to batch data
        results.forEach(result => {
            const { walletAddress, ...walletData } = result;
            batchData[walletAddress] = walletData;
        });

        console.log(`✅ Batch fetched tokens for ${addressesToFetch.length} wallets successfully (${validAddresses.length} total)`);

        return {
            status: 'success',
            data: batchData
        };

    } catch (error) {
        console.error('Error in batch wallet tokens fetch:', error);
        return {
            status: 'error',
            error: error instanceof Error ? error.message : 'Unknown error',
            data: null
        };
    }
};

/**
 * Get wallet tokens with full metadata
 * @param walletAddress The wallet address to fetch tokens for
 * @returns Token array with metadata (name, symbol, image, etc.)
 */
export const getWalletTokensWithMetadata = async (walletAddress: string) => {
    try {
        // Validate the wallet address
        if (!walletAddress || typeof walletAddress !== 'string' || walletAddress.trim() === '') {
            console.error('Invalid wallet address provided to getWalletTokensWithMetadata');
            return {
                status: 'error',
                error: 'Invalid wallet address',
                data: null
            };
        }

        // Use the client SDK instead of direct fetch to avoid CORS issues
        try {
            // Get wallet data using direct API call
            const response = await fetch(`${BASE_URL}/wallet/${walletAddress.trim()}`, {
                method: 'GET',
                headers: {
                    'x-api-key': API_KEY,
                    'Content-Type': 'application/json'
                }
            });

            if (!response.ok) {
                throw new Error(`Solana Tracker API error: ${response.status} ${response.statusText}`);
            }

            const wallet = await response.json();

            if (!wallet || !wallet.tokens || !Array.isArray(wallet.tokens)) {
                console.warn(`No token data found for wallet ${walletAddress}`);
                return {
                    status: 'success',
                    data: {
                        tokens: [],
                        total: wallet?.total || 0,
                        totalSol: wallet?.totalSol || 0
                    }
                };
            }

            // Process the token data to format it consistently
            const processedTokens = wallet.tokens.map((tokenItem: any) => {
                // Extract token data from the response
                const token = tokenItem.token || {};
                const balance = tokenItem.balance || 0;
                const value = tokenItem.value || 0;

                return {
                    mint: token.mint || '',
                    name: token.name || 'Unknown Token',
                    symbol: token.symbol || '',
                    decimals: token.decimals || 9,
                    image: token.image || null,
                    balance: balance.toString(),
                    uiBalance: balance.toString(), // Human-readable balance
                    value: value, // USD value
                    price: tokenItem.price || {
                        quote: 0,
                        usd: 0
                    }
                };
            });

            return {
                status: 'success',
                data: {
                    tokens: processedTokens,
                    total: wallet.total || 0, // Total USD value
                    totalSol: wallet.totalSol || 0 // Total SOL value (excluding actual SOL)
                }
            };
        } catch (clientError) {
            console.error('Client SDK request failed:', clientError);

            // Fall back to a simpler approach using token info directly
            try {
                return {
                    status: 'success',
                    data: {
                        tokens: [],
                        total: 0,
                        totalSol: 0
                    }
                };
            } catch (fallbackError) {
                console.error('Fallback token request also failed:', fallbackError);
                throw fallbackError;
            }
        }
    } catch (error) {
        console.error('Error fetching wallet tokens with metadata:', error);
        return {
            status: 'error',
            error: error instanceof Error ? error.message : 'Unknown error',
            data: {
                tokens: [],
                total: 0,
                totalSol: 0
            }
        };
    }
};

/**
 * Get all token holders with pagination
 * @param tokenAddress The token mint address
 * @param page Page number (starting from 1)
 * @param limit Number of holders per page
 */
export const getTokenHoldersPaginated = async (tokenAddress: string, page: number = 1, limit: number = 50): Promise<ApiResponse<any>> => {
    try {
        // Validate token address
        if (!tokenAddress || typeof tokenAddress !== 'string' || tokenAddress.trim() === '') {
            return {
                status: 'error',
                error: 'Invalid token address',
                data: null
            };
        }

        // Looking at the existing implementation elsewhere in the file
        // Use direct API call for token holders
        const response = await fetch(`${BASE_URL}/tokens/${tokenAddress.trim()}/holders`, {
            method: 'GET',
            headers: {
                'x-api-key': API_KEY,
                'Content-Type': 'application/json'
            }
        });

        if (!response.ok) {
            throw new Error(`Solana Tracker API error: ${response.status} ${response.statusText}`);
        }

        const holdersData = await response.json();

        // Handle pagination in our service layer
        // Limit to the specified number
        if (holdersData && holdersData.accounts) {
            const startIndex = (page - 1) * limit;
            const endIndex = startIndex + limit;
            holdersData.accounts = holdersData.accounts.slice(startIndex, endIndex);
        }

        return {
            status: 'success',
            data: holdersData
        };
    } catch (error) {
        console.error('Error fetching token holders:', error);
        return {
            status: 'error',
            error: error instanceof Error ? error.message : 'Unknown error',
            data: null
        };
    }
};

/**
 * Get top holders for a token
 * @param tokenAddress The token mint address
 */
export const getTokenTopHolders = async (tokenAddress: string): Promise<ApiResponse<any>> => {
    try {
        // Validate token address
        if (!tokenAddress || typeof tokenAddress !== 'string' || tokenAddress.trim() === '') {
            return {
                status: 'error',
                error: 'Invalid token address',
                data: null
            };
        }


        // Use direct API call for top holders
        const response = await fetch(`${BASE_URL}/tokens/${tokenAddress.trim()}/holders/top`, {
            method: 'GET',
            headers: {
                'x-api-key': API_KEY,
                'Content-Type': 'application/json'
            }
        });

        if (!response.ok) {
            throw new Error(`Solana Tracker API error: ${response.status} ${response.statusText}`);
        }

        const topHoldersData = await response.json();

        return {
            status: 'success',
            data: topHoldersData
        };
    } catch (error) {
        console.error('Error fetching top token holders:', error);
        return {
            status: 'error',
            error: error instanceof Error ? error.message : 'Unknown error',
            data: null
        };
    }
};

/**
 * Get first buyers (snipers) for a token
 * @param tokenAddress The token mint address
 */
export const getTokenFirstBuyers = async (tokenAddress: string): Promise<ApiResponse<any>> => {
    try {
        // Validate token address
        if (!tokenAddress || typeof tokenAddress !== 'string' || tokenAddress.trim() === '') {
            return {
                status: 'error',
                error: 'Invalid token address',
                data: null
            };
        }


        // Use direct API call for first buyers
        const response = await fetch(`${BASE_URL}/first-buyers/${tokenAddress.trim()}`, {
            method: 'GET',
            headers: {
                'x-api-key': API_KEY,
                'Content-Type': 'application/json'
            }
        });

        if (!response.ok) {
            throw new Error(`Solana Tracker API error: ${response.status} ${response.statusText}`);
        }

        const firstBuyersData = await response.json();

        return {
            status: 'success',
            data: firstBuyersData
        };
    } catch (error) {
        console.error('Error fetching token first buyers:', error);
        return {
            status: 'error',
            error: error instanceof Error ? error.message : 'Unknown error',
            data: null
        };
    }
};

/**
 * Get wallet trades with pagination
 * @param walletAddress The wallet address to fetch trades for
 * @param limit Number of trades to fetch (optional, default 20)
 * @param page Page number (optional, starts from 1)
 * @returns Trades with detailed information
 */
export const getWalletTradesWithPagination = async (
    walletAddress: string,
    limit: number = 20,
    page: number = 1
): Promise<ApiResponse<any>> => {
    try {
        // Validate the wallet address
        if (!walletAddress || typeof walletAddress !== 'string' || walletAddress.trim() === '') {
            console.error('Invalid wallet address provided to getWalletTradesWithPagination');
            return {
                status: 'error',
                error: 'Invalid wallet address',
                data: {
                    trades: []
                }
            };
        }
        // Use direct API call for wallet trades
        const response = await fetch(`${BASE_URL}/wallet/${walletAddress.trim()}/trades?showMeta=true&parseJupiter=true`, {
            method: 'GET',
            headers: {
                'x-api-key': API_KEY,
                'Content-Type': 'application/json'
            }
        });

        if (!response.ok) {
            throw new Error(`Solana Tracker API error: ${response.status} ${response.statusText}`);
        }

        const trades = await response.json();

        // If the API doesn't support pagination directly, handle it manually
        if (trades && Array.isArray(trades.trades)) {
            const startIndex = (page - 1) * limit;
            const endIndex = startIndex + limit;

            // Apply pagination manually
            const paginatedTrades = trades.trades.slice(startIndex, endIndex);

            return {
                status: 'success',
                data: {
                    trades: paginatedTrades,
                    total: trades.trades.length,
                    page,
                    limit,
                    hasMore: endIndex < trades.trades.length
                }
            };
        }

        return {
            status: 'success',
            data: trades
        };
    } catch (error) {
        console.error('Error fetching wallet trades with pagination:', error);
        return {
            status: 'error',
            error: error instanceof Error ? error.message : 'Unknown error',
            data: {
                trades: []
            }
        };
    }
};

/**
 * Get wallet profit and loss data with detailed breakdowns
 * @param walletAddress The wallet address to fetch P&L data for
 * @param includeHistory Include historical data
 * @returns Detailed P&L data for the wallet
 */
export const getWalletPnLDetailed = async (walletAddress: string, includeHistory: boolean = true): Promise<ApiResponse<any>> => {
    try {
        // Validate the wallet address
        if (!walletAddress || typeof walletAddress !== 'string' || walletAddress.trim() === '') {
            console.error('Invalid wallet address provided to getWalletPnLDetailed');
            return {
                status: 'error',
                error: 'Invalid wallet address',
                data: null
            };
        }

        console.log(`🔍 Fetching detailed P&L data for wallet: ${walletAddress.slice(0, 8)}...`);

        try {
            // Use the simple working endpoint
            const url = `${BASE_URL}/pnl/${walletAddress.trim()}`;
            console.log(`🔄 Fetching detailed P&L from: ${url}`);

            const response = await fetch(url, {
                method: 'GET',
                headers: {
                    'x-api-key': API_KEY,
                    'Content-Type': 'application/json'
                }
            });

            if (response.ok) {
                const pnlData = await response.json() as PnLResponse;

                // Process the data to add useful derived fields if needed
                if (pnlData && pnlData.positions) {
                    let totalPnL = 0;
                    let totalInvested = 0;

                    pnlData.positions.forEach((position: PnLPosition) => {
                        if (position.pnl && typeof position.pnl.usd === 'number') {
                            totalPnL += position.pnl.usd;
                        }

                        if (position.invested && typeof position.invested.usd === 'number') {
                            totalInvested += position.invested.usd;
                        }
                    });

                    // Add these totals to the response
                    if (!pnlData.summary) {
                        pnlData.summary = {};
                    }

                    pnlData.summary.totalPnL = totalPnL;
                    pnlData.summary.totalInvested = totalInvested;
                    pnlData.summary.pnlPercentage = totalInvested > 0 ? (totalPnL / totalInvested) * 100 : 0;
                }

                console.log(`✅ Detailed P&L data fetched successfully for wallet ${walletAddress.slice(0, 8)}...`);
                return {
                    status: 'success',
                    data: pnlData
                };
            } else {
                console.warn(`⚠️ Detailed P&L API failed with status ${response.status}: ${response.statusText}`);
                throw new Error(`API responded with ${response.status}: ${response.statusText}`);
            }
        } catch (error) {
            console.error(`❌ Detailed P&L API call failed:`, error);

            // Return empty structure if API fails
            console.log(`🔄 Returning empty detailed P&L structure for wallet ${walletAddress.slice(0, 8)}...`);
            return {
                status: 'success',
                data: {
                    positions: [],
                    summary: {
                        totalPnL: 0,
                        totalInvested: 0,
                        pnlPercentage: 0
                    }
                }
            };
        }
    } catch (error) {
        console.error('Error fetching detailed P&L data:', error);
        return {
            status: 'error',
            error: error instanceof Error ? error.message : 'Unknown error',
            data: null
        };
    }
};

/**
 * Get token transactions with better pagination support
 * @param tokenAddress The token mint address
 * @param limit Optional limit for number of transactions (default is 100)
 * @param cursor Optional cursor for pagination
 * @returns List of transactions with details
 */
export const getTokenTransactions = async (
    tokenAddress: string,
    limit: number = 100,
    cursor?: string
): Promise<ApiResponse<any>> => {
    try {
        // Validate token address
        if (!tokenAddress || typeof tokenAddress !== 'string' || tokenAddress.trim() === '') {
            return {
                status: 'error',
                error: 'Invalid token address',
                data: null
            };
        }


        // Use direct API call for token trades
        const response = await fetch(`${BASE_URL}/trades/${tokenAddress.trim()}?showMeta=true&parseJupiter=true`, {
            method: 'GET',
            headers: {
                'x-api-key': API_KEY,
                'Content-Type': 'application/json'
            }
        });

        if (!response.ok) {
            throw new Error(`Solana Tracker API error: ${response.status} ${response.statusText}`);
        }

        const transactionsData = await response.json();

        // Check if we need to fetch more data (if API supports pagination)
        const hasNextPage = transactionsData.hasNextPage || false;
        const nextCursor = transactionsData.nextCursor || null;

        return {
            status: 'success',
            data: {
                trades: transactionsData.trades || [],
                hasNextPage,
                nextCursor
            }
        };
    } catch (error) {
        console.error('Error fetching token transactions:', error);
        return {
            status: 'error',
            error: error instanceof Error ? error.message : 'Unknown error',
            data: null
        };
    }
};

/**
 * Load all available transaction history for a token by handling pagination automatically
 * @param tokenAddress The token mint address
 * @param maxTransactions Maximum number of transactions to fetch (optional)
 * @returns Complete list of transactions
 */
export const getAllTokenTransactions = async (
    tokenAddress: string,
    maxTransactions: number = 500
): Promise<ApiResponse<any>> => {
    try {
        let allTransactions: any[] = [];
        let hasMore = true;
        let nextCursor: string | null = null;
        let requestCount = 0;
        const MAX_REQUESTS = 5; // Limit number of pagination requests to avoid excessive API calls


        // Fetch transactions with pagination
        while (hasMore && allTransactions.length < maxTransactions && requestCount < MAX_REQUESTS) {
            const result = await getTokenTransactions(tokenAddress, 100, nextCursor || undefined);
            requestCount++;

            if (result.status === 'success' && result.data && result.data.trades) {
                // Add new transactions to our collection
                allTransactions = [...allTransactions, ...result.data.trades];

                // Check if there are more pages
                hasMore = result.data.hasNextPage || false;
                nextCursor = result.data.nextCursor || null;

                if (!nextCursor) {
                    hasMore = false;
                }
            } else {
                console.warn('Failed to fetch transactions page:', result);
                hasMore = false;
            }
        }


        // Sort transactions by time (newest first)
        allTransactions.sort((a, b) => b.time - a.time);

        // Trim to max requested size
        if (allTransactions.length > maxTransactions) {
            allTransactions = allTransactions.slice(0, maxTransactions);
        }

        return {
            status: 'success',
            data: {
                trades: allTransactions,
                total: allTransactions.length
            }
        };
    } catch (error) {
        console.error('Error fetching all token transactions:', error);
        return {
            status: 'error',
            error: error instanceof Error ? error.message : 'Unknown error',
            data: null
        };
    }
};

/**
 * Get first buyers with detailed info
 * @param tokenAddress The token mint address
 * @returns List of first buyers with transaction details
 */
export const getDetailedFirstBuyers = async (tokenAddress: string): Promise<ApiResponse<any>> => {
    try {
        // Validate token address
        if (!tokenAddress || typeof tokenAddress !== 'string' || tokenAddress.trim() === '') {
            return {
                status: 'error',
                error: 'Invalid token address',
                data: null
            };
        }
        // Use direct API call for first buyers
        const response = await fetch(`${BASE_URL}/first-buyers/${tokenAddress.trim()}`, {
            method: 'GET',
            headers: {
                'x-api-key': API_KEY,
                'Content-Type': 'application/json'
            }
        });

        if (!response.ok) {
            throw new Error(`Solana Tracker API error: ${response.status} ${response.statusText}`);
        }

        const firstBuyersData = await response.json();

        return {
            status: 'success',
            data: firstBuyersData
        };
    } catch (error) {
        console.error('Error fetching detailed first buyers:', error);
        return {
            status: 'error',
            error: error instanceof Error ? error.message : 'Unknown error',
            data: null
        };
    }
};

/**
 * Forcefully refresh wallet tokens data from the API without caching
 * @param walletAddress The wallet address to refresh tokens for
 * @param tokenMint Optional specific token mint to refresh
 * @param forceRefresh Force a fresh fetch bypassing API cache
 * @returns Token array with metadata
 */
export const refreshWalletTokens = async (walletAddress: string, tokenMint?: string, forceRefresh = false): Promise<ApiResponse<any>> => {
    try {
        // Validate the wallet address
        if (!walletAddress || typeof walletAddress !== 'string' || walletAddress.trim() === '') {
            console.error('Invalid wallet address provided to refreshWalletTokens');
            return {
                status: 'error',
                error: 'Invalid wallet address',
                data: null
            };
        }
        // Generate a timestamp for cache-busting if needed
        const timestamp = Date.now();
        let wallet: any = null;

        // Try proxy service first
        try {
            wallet = await proxyService.fetchSolanaTrackerWallet(walletAddress.trim());
            if (wallet) {
            } else {
            }
        } catch (proxyError) {
            console.error('Error fetching via proxy:', proxyError);
        }

        // If proxy fails, try direct client
        if (!wallet) {
            try {
                const response = await fetch(`${BASE_URL}/wallet/${walletAddress.trim()}`, {
                    method: 'GET',
                    headers: {
                        'x-api-key': API_KEY,
                        'Content-Type': 'application/json'
                    }
                });

                if (!response.ok) {
                    throw new Error(`Solana Tracker API error: ${response.status} ${response.statusText}`);
                }

                wallet = await response.json();
            } catch (clientError) {
                console.error('Client SDK request failed:', clientError);
                return {
                    status: 'error',
                    error: clientError instanceof Error ? clientError.message : 'Failed to fetch wallet data',
                    data: null
                };
            }
        }

        // Check if we have data
        if (!wallet || !wallet.tokens || !Array.isArray(wallet.tokens)) {
            console.warn(`No token data found for wallet ${walletAddress}`);
            return {
                status: 'success',
                data: {
                    tokens: [],
                    total: wallet?.total || 0,
                    totalSol: wallet?.totalSol || 0
                }
            };
        }

        // Filter for a specific token if requested
        let tokens = wallet.tokens;
        if (tokenMint) {
            tokens = tokens.filter((tokenItem: any) =>
                tokenItem.token && tokenItem.token.mint === tokenMint
            );
        }

        // Process the token data to format it consistently
        const processedTokens = tokens.map((tokenItem: any) => {
            // Extract token data from the response
            const token = tokenItem.token || {};
            const balance = tokenItem.balance || 0;
            const value = tokenItem.value || 0;

            return {
                mint: token.mint || '',
                name: token.name || 'Unknown Token',
                symbol: token.symbol || '',
                decimals: token.decimals || 9,
                image: token.image || null,
                balance: balance.toString(),
                uiBalance: balance.toString(), // Human-readable balance
                value: value, // USD value
                price: tokenItem.price || {
                    quote: 0,
                    usd: 0
                }
            };
        });

        // Dispatch an event to notify that we've updated the token data
        try {
            const event = new CustomEvent('ekko:wallet_tokens_refreshed', {
                detail: {
                    address: walletAddress,
                    tokenMint: tokenMint,
                    timestamp
                }
            });
            window.dispatchEvent(event);
        } catch (eventError) {
            console.error('Failed to dispatch token refresh event:', eventError);
        }

        return {
            status: 'success',
            data: {
                tokens: processedTokens,
                total: wallet.total || 0,
                totalSol: wallet.totalSol || 0,
                timestamp
            }
        };
    } catch (error) {
        console.error('Error refreshing wallet tokens:', error);
        return {
            status: 'error',
            error: error instanceof Error ? error.message : 'Unknown error',
            data: null
        };
    }
};

/**
 * Fetch token data from Solana Tracker API
 * @param tokenAddress Token mint address
 * @returns Token data including price, market cap, liquidity, etc.
 */
export async function getTokenData(tokenAddress: string) {
    try {
        const response = await fetch(`${BASE_URL}/tokens/${tokenAddress}`, {
            method: 'GET',
            headers: {
                'x-api-key': API_KEY,
                'Content-Type': 'application/json'
            }
        });

        if (!response.ok) {
            throw new Error(`Solana Tracker API error: ${response.status} ${response.statusText}`);
        }

        const data = await response.json();
        return {
            status: 'success',
            data
        };
    } catch (error) {
        console.error('Error fetching token data:', error);
        return {
            status: 'error',
            error: error instanceof Error ? error.message : 'Unknown error'
        };
    }
}

/**
 * Extract token stats from Solana Tracker API response
 * @param tokenData Response from getTokenData
 * @returns Structured token stats
 */
export function extractTokenStats(tokenData: any) {
    if (!tokenData || !tokenData.status || tokenData.status !== 'success' || !tokenData.data) {
        return null;
    }

    const { data } = tokenData;

    // Find the main pool (usually the one with highest liquidity)
    const mainPool = data.pools && data.pools.length > 0
        ? data.pools.sort((a: any, b: any) =>
            (b.liquidity?.usd || 0) - (a.liquidity?.usd || 0)
        )[0]
        : null;

    if (!mainPool) {
        return null;
    }

    return {
        token: {
            name: data.token?.name || 'Unknown',
            symbol: data.token?.symbol || 'UNKNOWN',
            mint: data.token?.mint || tokenData.tokenAddress,
            decimals: data.token?.decimals || 9,
            image: data.token?.image || '',
            description: data.token?.description || ''
        },
        price: mainPool.price?.usd || 0,
        marketCap: mainPool.marketCap?.usd || 0,
        liquidity: mainPool.liquidity?.usd || 0,
        transactions: {
            buys: mainPool.txns?.buys || 0,
            sells: mainPool.txns?.sells || 0,
            total: mainPool.txns?.total || 0,
            volume: mainPool.txns?.volume || 0
        },
        lastUpdated: mainPool.lastUpdated || Date.now()
    };
}

/**
 * Get a specific token balance for a wallet using Solana Tracker
 * This replaces the Shyft getTokenBalance function
 * @param walletAddress The wallet address
 * @param tokenMint The token mint address
 * @param forceRefresh Force a fresh fetch (ignored for now, but kept for compatibility)
 * @returns The token balance as a string, or '0' if not found
 */
export const getTokenBalance = async (
    walletAddress: string,
    tokenMint: string,
    forceRefresh: boolean = false
): Promise<string> => {
    try {
        console.log(`🔍 [SolanaTracker] Getting token balance for ${tokenMint} in wallet ${walletAddress.slice(0, 8)}...`);

        // Method 1: Try getting all wallet tokens using Solana Tracker
        let walletTokensResponse = await getWalletTokensWithMetadata(walletAddress);

        if (walletTokensResponse.status !== 'success' || !walletTokensResponse.data) {
            console.warn(`⚠️ [SolanaTracker] Failed to get wallet tokens: ${walletTokensResponse.error}`);

            // Method 2: If force refresh is requested, try refreshing wallet tokens
            if (forceRefresh) {
                console.log(`🔄 [SolanaTracker] Force refresh requested, trying refreshWalletTokens...`);
                const refreshResult = await refreshWalletTokens(walletAddress, tokenMint, true);

                if (refreshResult.status === 'success' && refreshResult.data?.tokens) {
                    const targetToken = refreshResult.data.tokens.find((t: any) => t.mint === tokenMint);
                    if (targetToken && targetToken.balance) {
                        const balance = targetToken.balance || targetToken.uiBalance || '0';
                        console.log(`✅ [SolanaTracker] Found balance via refresh: ${balance} for ${tokenMint}`);
                        return balance;
                    }
                }
            }

            return '0';
        }

        // Find the specific token in the wallet
        const targetToken = walletTokensResponse.data.tokens.find(
            (token: any) => token.mint === tokenMint
        );

        if (!targetToken) {
            console.log(`📭 [SolanaTracker] Token ${tokenMint} not found in wallet ${walletAddress.slice(0, 8)}...`);

            // Method 3: If token not found and force refresh requested, try one more time
            if (forceRefresh) {
                console.log(`🔄 [SolanaTracker] Token not found, trying force refresh...`);
                const refreshResult = await refreshWalletTokens(walletAddress, tokenMint, true);

                if (refreshResult.status === 'success' && refreshResult.data?.tokens) {
                    const refreshedToken = refreshResult.data.tokens.find((t: any) => t.mint === tokenMint);
                    if (refreshedToken && refreshedToken.balance) {
                        const balance = refreshedToken.balance || refreshedToken.uiBalance || '0';
                        console.log(`✅ [SolanaTracker] Found balance via force refresh: ${balance} for ${tokenMint}`);
                        return balance;
                    }
                }
            }

            return '0';
        }

        const balance = targetToken.balance || targetToken.uiBalance || '0';
        console.log(`✅ [SolanaTracker] Found token balance: ${balance} for ${tokenMint}`);

        return balance;

    } catch (error) {
        console.error(`❌ [SolanaTracker] Error getting token balance for ${tokenMint}:`, error);
        return '0';
    }
};

// Duplicate implementation was removed to resolve TypeScript errors.
// The main implementation above is now used for all chart data requests.